import { useState } from "react";
import { clsx } from "clsx";
import { Play } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { pageImageUrl, useToolCatalog } from "../api/pdfApi";
import type { PdfDocument } from "../types";

/** Sześć miejsc, w których numer wygląda sensownie — ułamki strony (x, y od góry). */
const POSITIONS: { value: string; x: number; y: number; label: string }[] = [
  { value: "top-left", x: 0.05, y: 0.04, label: "góra lewo" },
  { value: "top-center", x: 0.5, y: 0.04, label: "góra środek" },
  { value: "top-right", x: 0.95, y: 0.04, label: "góra prawo" },
  { value: "bottom-left", x: 0.05, y: 0.97, label: "dół lewo" },
  { value: "bottom-center", x: 0.5, y: 0.97, label: "dół środek" },
  { value: "bottom-right", x: 0.95, y: 0.97, label: "dół prawo" },
];

const FORMATS = [
  { value: "n", label: "1" },
  { value: "n_of_total", label: "1/10" },
  { value: "str_n", label: "str. 1" },
  { value: "str_n_of_total", label: "str. 1/10" },
  { value: "pl", label: "Strona 1 z 10" },
];

const SIZES = [6, 7, 8, 9, 10, 11, 12, 14, 16, 18, 20, 24];
const COLORS = ["#333333", "#111111", "#dc1f1f", "#1f7ddc", "#888888"];

function sample(format: string, page: number, total: number): string {
  switch (format) {
    case "n_of_total":
      return `${page}/${total}`;
    case "str_n":
      return `str. ${page}`;
    case "str_n_of_total":
      return `str. ${page}/${total}`;
    case "pl":
      return `Strona ${page} z ${total}`;
    default:
      return `${page}`;
  }
}

/**
 * Numerowanie stron z podglądem NA STRONIE: klika się miejsce na miniaturze,
 * a napis od razu staje tam, gdzie stanie w pliku — z wybranym krojem, stopniem
 * pisma i kolorem. Wcześniej trzeba było wybrać „bottom-center” z listy i uwierzyć.
 */
export function PageNumbersModal({
  document: doc,
  ticket,
  open,
  onClose,
  onRun,
  running,
}: {
  document: PdfDocument;
  ticket: string | undefined;
  open: boolean;
  onClose: () => void;
  onRun: (params: Record<string, unknown>) => void;
  running: boolean;
}) {
  // Domyślnie „jak w dokumencie”: serwer sam odczytuje istniejącą numerację
  // (format, krój, stopień pisma i miejsce) i powtarza ją. Numer dołożony
  // w cudzym kroju wygląda jak łata — właściciel wytknął to wprost.
  const [position, setPosition] = useState("auto");
  const [format, setFormat] = useState("auto");
  const [font, setFont] = useState("auto");
  const [size, setSize] = useState(0);
  const [bold, setBold] = useState(false);
  const [italic, setItalic] = useState(false);
  const [color, setColor] = useState(COLORS[0]);
  const [startAt, setStartAt] = useState(1);
  const [skipFirst, setSkipFirst] = useState(false);

  const { data: catalog } = useToolCatalog();
  const fonts = catalog?.fonts ?? [];
  const total = doc.page_count || 1;
  const spot = POSITIONS.find((p) => p.value === position) ?? POSITIONS[4];
  const jakWDokumencie = position === "auto" || format === "auto" || font === "auto" || !size;
  const label = sample(format === "auto" ? "pl" : format, startAt, total);

  return (
    <Modal open={open} onClose={onClose} title="Numeruj strony" size="lg">
      <div className="flex flex-col gap-4 md:flex-row">
        <div className="shrink-0">
          <p className="section-label mb-1.5">
            {jakWDokumencie
              ? "Domyślnie jak w dokumencie — kliknij, żeby wybrać samemu"
              : "Kliknij, gdzie ma stanąć numer"}
          </p>
          <div className="relative mx-auto w-[240px] border border-[var(--color-border-strong)] bg-white">
            {ticket ? (
              <img
                src={pageImageUrl(doc.id, 1, ticket, 480, doc.version_number)}
                alt="Podgląd strony"
                className="block w-full"
              />
            ) : (
              <div className="aspect-[1/1.41] w-full animate-pulse bg-[var(--color-bg-elevated)]" />
            )}
            {POSITIONS.map((p) => (
              <button
                key={p.value}
                type="button"
                aria-label={`Numer: ${p.label}`}
                aria-pressed={position === p.value}
                title={`${p.label} — wybór ręczny, numer stanie dokładnie tu`}
                onClick={() => setPosition(p.value)}
                style={{
                  left: `${p.x * 100}%`,
                  top: `${p.y * 100}%`,
                  transform: `translate(${p.x > 0.9 ? "-100%" : p.x < 0.1 ? "0" : "-50%"}, -50%)`,
                }}
                className={clsx(
                  "absolute h-4 w-8 border transition-colors",
                  position === p.value
                    ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)]"
                    : "border-dashed border-[var(--color-border-strong)] hover:border-[var(--color-primary)]",
                )}
              />
            ))}
            <span
              className="pointer-events-none absolute whitespace-pre leading-none"
              style={{
                left: `${spot.x * 100}%`,
                top: `${spot.y * 100}%`,
                transform: `translate(${spot.x > 0.9 ? "-100%" : spot.x < 0.1 ? "0" : "-50%"}, -50%)`,
                color,
                fontSize: `${Math.max(5, size * 0.55)}px`,
                fontFamily: fonts.find((f) => f.value === font)?.css,
                fontWeight: bold ? 700 : 400,
                fontStyle: italic ? "italic" : "normal",
              }}
            >
              {label}
            </span>
          </div>
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <div>
            <label htmlFor="pn-format" className="section-label mb-1 block">
              Format
            </label>
            <select
              id="pn-format" value={format} onChange={(e) => setFormat(e.target.value)}
              className="input-base py-1.5 text-xs"
            >
              <option value="auto">jak w dokumencie</option>
              {FORMATS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <div className="flex-1">
              <label htmlFor="pn-font" className="section-label mb-1 block">
                Krój
              </label>
              <select
                id="pn-font" value={font} onChange={(e) => setFont(e.target.value)}
                className="input-base py-1.5 text-xs"
              >
                <option value="auto">jak w dokumencie</option>
                {fonts.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-24">
              <label htmlFor="pn-size" className="section-label mb-1 block">
                Wielkość
              </label>
              <select
                id="pn-size" value={size} onChange={(e) => setSize(Number(e.target.value))}
                className="input-base py-1.5 text-xs"
              >
                <option value={0}>jak w dok.</option>
                {SIZES.map((v) => (
                  <option key={v} value={v}>
                    {v} pkt
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-end gap-2">
            <span className="flex gap-1">
              <button
                type="button" aria-label="Pogrubienie" aria-pressed={bold}
                onClick={() => setBold((v) => !v)}
                className={clsx(
                  "border px-2.5 py-1.5 text-xs font-bold",
                  bold
                    ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)] text-[var(--color-primary)]"
                    : "border-[var(--color-border)] text-[var(--color-text-secondary)]",
                )}
              >
                B
              </button>
              <button
                type="button" aria-label="Kursywa" aria-pressed={italic}
                onClick={() => setItalic((v) => !v)}
                className={clsx(
                  "border px-2.5 py-1.5 text-xs italic",
                  italic
                    ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)] text-[var(--color-primary)]"
                    : "border-[var(--color-border)] text-[var(--color-text-secondary)]",
                )}
              >
                I
              </button>
            </span>
            <span className="flex items-center gap-1">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Kolor ${c}`}
                  onClick={() => setColor(c)}
                  style={{ background: c }}
                  className={clsx(
                    "h-6 w-6 border",
                    color === c ? "border-[var(--color-text-primary)]" : "border-[var(--color-border)]",
                  )}
                />
              ))}
            </span>
          </div>

          <div className="flex gap-2">
            <div className="w-28">
              <label htmlFor="pn-start" className="section-label mb-1 block">
                Zacznij od
              </label>
              <input
                id="pn-start" type="number" min={0} value={startAt}
                onChange={(e) => setStartAt(Number(e.target.value) || 0)}
                className="input-base py-1.5 text-xs"
              />
            </div>
            <label className="flex cursor-pointer items-end gap-2 pb-2 text-xs">
              <input
                type="checkbox" checked={skipFirst}
                onChange={(e) => setSkipFirst(e.target.checked)}
                className="h-3.5 w-3.5 accent-[var(--color-primary)]"
              />
              <span className="text-[var(--color-text-secondary)]">
                Pomiń pierwszą stronę (okładkę)
              </span>
            </label>
          </div>

          <div className="flex items-center justify-between border-t border-[var(--color-border)] pt-3">
            <span className="text-[11px] text-[var(--color-text-muted)]">
              Podgląd: <span className="font-medium text-[var(--color-text-primary)]">{label}</span>
              {" · "}
              {POSITIONS.find((p) => p.value === position)?.label}
            </span>
            <span className="flex gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={onClose}>
                Anuluj
              </Button>
              <Button
                type="button" size="sm" loading={running}
                onClick={() =>
                  onRun({
                    position, format, font, size, bold, italic, color,
                    start_at: startAt, skip_first: skipFirst,
                  })
                }
              >
                <Play size={13} className="mr-1.5" /> Ponumeruj
              </Button>
            </span>
          </div>
        </div>
      </div>
    </Modal>
  );
}
